/**
 * A code, shown for a minute and then hidden again.
 *
 * The value arrives from POST /api/secrets/:id/reveal, which writes the audit row before it
 * decrypts anything, so every time this button is pressed somebody can later answer "who saw this
 * code". The value lives in component state and nowhere else: it is never written to storage,
 * never put in a URL, never logged, and it is cleared when the minute is up or the component
 * unmounts. A phone left on a dashboard shows a code for sixty seconds and then shows nothing.
 *
 * THE MINUTE IS A DEADLINE, NOT A COUNT OF TICKS. A timer that counts down one per tick stops
 * counting when the phone locks or the tab goes to the background: iOS Safari suspends it and
 * Chrome throttles it to once a minute after five minutes hidden, so a code revealed at second five
 * and looked at again ten minutes later would still be drawn with "hidden again in 55 seconds".
 * Instead the reveal records the instant it must be hidden by, every tick derives what is left from
 * that, and the page re-checks the moment it becomes visible again. The one wall-clock read here is
 * a screen timer and is never written anywhere.
 *
 * The three refusals are three different sentences, and all of them are the server's. A cleaner
 * outside her window, or asking for a code that is not hers, gets a 404 and cannot tell the two
 * apart, which is deliberate. An office person with a stale session gets a link to sign in again.
 * A deployment with no cipher key gets told which command an owner runs.
 */
import { useEffect, useState } from 'react';
import { ApiError, needsStepUp, revealSecret, type RevealedSecret } from '../api.ts';
import { REVEAL_SECONDS, countdownWords } from '../format.ts';
import { Button } from './Field.tsx';
import { ErrorNotice, StepUpNotice } from './Notice.tsx';

/** A revealed value and the instant, in epoch milliseconds, by which it has to be gone. */
type Shown = { readonly secret: RevealedSecret; readonly hideAt: number };

export function RevealCode({ secretId, returnPath }: { secretId: string; returnPath: string }) {
  const [shown, setShown] = useState<Shown | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!shown) return;
    const { hideAt } = shown;
    const check = () => {
      const left = Math.ceil((hideAt - Date.now()) / 1000);
      if (left <= 0) setShown(null);
      else setSecondsLeft(left);
    };
    check();
    const timer = setInterval(check, 1000);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, [shown]);

  async function show() {
    setBusy(true);
    setError(null);
    try {
      const answer = await revealSecret(secretId, returnPath);
      setSecondsLeft(REVEAL_SECONDS);
      setShown({ secret: answer, hideAt: Date.now() + REVEAL_SECONDS * 1000 });
    } catch (thrown) {
      setError(
        thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The code did not load.'),
      );
    } finally {
      setBusy(false);
    }
  }

  if (shown) {
    return (
      <div className="mt-2 rounded-lg border border-gold/50 bg-gold/10 p-3">
        <p className="font-mono text-2xl tracking-widest break-all select-all">
          {shown.secret.value}
        </p>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-navy/70" role="status">
            Hidden again in {countdownWords(secondsLeft)}.
          </p>
          <Button tone="quiet" onClick={() => setShown(null)}>
            Hide it now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2">
      {error ? (
        <div className="mb-2">
          {needsStepUp(error) ? (
            <StepUpNotice error={error} />
          ) : (
            <ErrorNotice title="Not shown" message={error.message} />
          )}
        </div>
      ) : null}
      <Button onClick={show} disabled={busy}>
        {busy ? 'Asking' : 'Show'}
      </Button>
    </div>
  );
}
